import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, HeartHandshake, Sparkles, Palette, Award, Dumbbell, Wine, BedDouble, Loader2, Lock, GraduationCap, HeartPulse, Mountain, Cigarette, Scissors, FileSignature, Music } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

// 18+ Explore hub — KYC-gated (kyc_status='verified' && is_18_plus=true).
// SAFE list only: no firearms, drugs, betting, gambling, adoption, jail booking,
// prescription meds, or explicit content. That was the compliance stance chosen
// as the default when the user picked "18+ KYC section" without answering the
// legal question. Admin can extend the tile list later once policy is finalised.

const TILES = [
  { key: "dating",           label: "Dating (18+)",          Icon: HeartHandshake, to: "/dating",                                            grad: "from-pink-500 to-rose-600" },
  { key: "matrimonial",      label: "Matrimonial",           Icon: FileSignature,  to: "/search?category=matrimonial",                       grad: "from-fuchsia-500 to-purple-600" },
  { key: "tattoo",           label: "Tattoo studios",        Icon: Palette,        to: "/search?q=tattoo&category=service",                  grad: "from-stone-800 to-stone-950" },
  { key: "piercing",         label: "Body piercing",         Icon: Scissors,       to: "/search?q=piercing&category=service",                grad: "from-indigo-600 to-blue-800" },
  { key: "adventure",        label: "Adventure sports",      Icon: Mountain,       to: "/search?q=adventure sports&category=vacation",       grad: "from-emerald-500 to-teal-700" },
  { key: "gym",              label: "Gyms & fitness",        Icon: Dumbbell,       to: "/search?category=gym",                               grad: "from-red-500 to-orange-600" },
  { key: "bars",             label: "Bars & pubs",           Icon: Wine,           to: "/search?q=bar pub lounge",                           grad: "from-amber-700 to-yellow-900" },
  { key: "cigarettes",       label: "Cigarette shops",       Icon: Cigarette,      to: "/search?q=cigarette tobacco shop",                   grad: "from-stone-600 to-stone-800" },
  { key: "hotels",           label: "Hotels & rooms",        Icon: BedDouble,      to: "/search?category=hotel",                             grad: "from-sky-500 to-blue-700" },
  { key: "plastic_surgery",  label: "Plastic surgery",       Icon: HeartPulse,     to: "/search?q=plastic surgery cosmetic&category=doctor", grad: "from-rose-500 to-red-700" },
  { key: "biodata",          label: "Biodata generator",     Icon: Award,          to: "/matrimonial",                                        grad: "from-yellow-600 to-orange-700" },
  { key: "licence_services", label: "Licence services",      Icon: GraduationCap,  to: "/search?q=driving licence passport pan aadhaar",     grad: "from-blue-500 to-indigo-700" },
  { key: "concerts",         label: "Concerts & gigs",       Icon: Music,          to: "/search?category=music",                             grad: "from-violet-500 to-purple-700" },
];


export default function Explore18() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kyc, setKyc] = useState(null);   // {kyc_status, is_18_plus, ...}
  const [loading, setLoading] = useState(true);

  useSeo({ title: "18+ Explore · KYC-verified adults only", description: "Curated adult-only listings — dating, tattoo, adventure sports, bars, plastic surgery, licence services and more. KYC + 18+ verification required.", canonical: "/explore-18" });

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/kyc/status").then(r => setKyc(r.data)).catch(() => setKyc(null)).finally(() => setLoading(false));
  }, [user?.user_id]);

  if (!user) return (
    <GatedNotice
      title="Sign in to continue"
      body="The 18+ section is restricted to verified adults. Please sign in first."
      cta="Login" onClick={() => navigate("/auth")}
    />
  );
  if (loading) return <div className="max-w-md mx-auto py-20 text-center text-stone-400"><Loader2 className="inline animate-spin" /></div>;
  if (!kyc || kyc.kyc_status !== "verified" || !kyc.is_18_plus) return (
    <GatedNotice
      title="18+ KYC required"
      body="You need a completed identity verification confirming you are 18 or older to explore this section. It takes 60 seconds — submit an ID + selfie."
      cta="Start KYC" onClick={() => navigate("/kyc")}
    />
  );
  if (kyc.kyc_expired) return (
    <GatedNotice
      title="Renew your 18+ verification"
      body={`Your KYC is over 12 months old${kyc.kyc_expires_at ? ` (expired ${new Date(kyc.kyc_expires_at).toLocaleDateString()})` : ""}. Re-verify with a fresh ID + selfie to continue exploring.`}
      cta="Renew KYC" onClick={() => navigate("/kyc")}
    />
  );

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8" data-testid="explore-18-page">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5">18+</span>
        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5"><ShieldCheck size={11} /> KYC verified</span>
      </div>
      <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-3">Adult Explore</h1>
      <p className="text-stone-600 mt-1 max-w-xl">Curated adult-only listings from your neighbourhood. All items are safe, legal and within our marketplace policy.</p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="explore-18-grid">
        {TILES.map(t => {
          const Icon = t.Icon;
          return (
            <Link
              key={t.key}
              to={t.to}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${t.grad} text-white p-5 min-h-[130px] hover:-translate-y-0.5 transition-transform flex flex-col justify-between`}
              data-testid={`explore-18-tile-${t.key}`}
            >
              <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center backdrop-blur">
                <Icon size={20} />
              </div>
              <div className="heading font-extrabold text-base md:text-lg">{t.label}</div>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 text-[11px] text-stone-500 leading-relaxed max-w-3xl">
        <b>Marketplace policy:</b> Find2Near does not permit listings for firearms, controlled/prescription drugs, gambling services, adoption, sexually explicit content or any item prohibited by Indian law. Report suspected violations at <a href="mailto:trust@find2near.com" className="text-[#E05E36] font-semibold">trust@find2near.com</a>.
      </div>
    </div>
  );
}

function GatedNotice({ title, body, cta, onClick }) {
  return (
    <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="explore-18-gated">
      <div className="w-14 h-14 rounded-2xl bg-stone-900 text-white grid place-items-center mx-auto"><Lock size={22} /></div>
      <h1 className="heading text-2xl font-extrabold text-stone-900 mt-4">{title}</h1>
      <p className="text-sm text-stone-600 mt-2">{body}</p>
      <button
        onClick={onClick}
        className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white font-bold px-6 min-h-[44px]"
        data-testid="explore-18-cta"
      >
        <ShieldCheck size={14} /> {cta}
      </button>
    </div>
  );
}
